import React from 'react';
import ReactDOM from 'react-dom';
import {
  RiDownload2Line,
  RiFileExcel2Line,
  RiFileTextLine,
  RiPrinterLine,
  RiRefreshLine,
} from 'react-icons/ri';

export type LoadingOverlayIcon = 'download' | 'export' | 'pdf' | 'print' | 'sync';

interface LoadingOverlayModalProps {
  isOpen: boolean;
  icon?: LoadingOverlayIcon;
  title: string;
  message?: string;
  submessage?: string;
}

const ICON_MAP: Record<LoadingOverlayIcon, React.ElementType> = {
  download: RiDownload2Line,
  export: RiFileExcel2Line,
  pdf: RiFileTextLine,
  print: RiPrinterLine,
  sync: RiRefreshLine,
};

const LoadingOverlayModal: React.FC<LoadingOverlayModalProps> = ({
  isOpen,
  icon = 'download',
  title,
  message,
  submessage,
}) => {
  if (!isOpen) return null;

  const IconComponent = ICON_MAP[icon];

  const modalContent = (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 backdrop-blur-sm">
      <div className="bg-white rounded-2xl shadow-2xl p-8 max-w-md w-full mx-4">
        <div className="text-center">
          {/* Animated Icon */}
          <div className="relative inline-block mb-6">
            <div className="w-24 h-24 bg-primary-100 rounded-xl flex items-center justify-center">
              <IconComponent className={`w-12 h-12 text-primary-600 ${icon === 'sync' ? 'animate-spin' : 'animate-bounce'}`} />
            </div>
            {/* Spinning circle loader */}
            <div className="absolute -top-2 -right-2">
              <svg className="w-8 h-8 animate-spin" viewBox="0 0 24 24">
                <circle
                  className="opacity-25"
                  cx="12" cy="12" r="10"
                  stroke="currentColor" strokeWidth="4" fill="none"
                />
                <path
                  className="opacity-75 text-primary-600"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                />
              </svg>
            </div>
          </div>

          <h3 className="text-xl font-semibold text-secondary-800 mb-2">
            {title}
          </h3>
          {message && (
            <p className="text-secondary-600 mb-2">{message}</p>
          )}
          {submessage && (
            <p className="text-sm text-secondary-500">
              {submessage}
            </p>
          )}

          {/* Animated dots */}
          <div className="flex justify-center mt-4 space-x-1">
            <div className="w-2 h-2 bg-primary-600 rounded-full animate-bounce" style={{ animationDelay: '0s' }} />
            <div className="w-2 h-2 bg-primary-600 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }} />
            <div className="w-2 h-2 bg-primary-600 rounded-full animate-bounce" style={{ animationDelay: '0.4s' }} />
          </div>
        </div>
      </div>
    </div>
  );

  return ReactDOM.createPortal(modalContent, document.body);
};

export default LoadingOverlayModal;
